import Country from '../Shipping/Country';
import State from '../Shipping/State';
import { nullSafeToString } from '../lib/util';

/** Prefix the API uses to identify an address belonging to a user. */
export const USER_ADDRESS_PREFIX = 'ADD_';

/** Prefix the API uses to identify an address belonging to a division. */
export const DIVISION_ADDRESS_PREFIX = 'DIV_';

const PREFIXED_ID_PATTERN = /^(?<prefix>ADD_|DIV_)(?<id>\d+)$/u;

// Country and state arrive either as an id or as a nested object, depending on whether the address
// came from the API or from a consumer's form.
const toId = (id, entity) => id || (entity && entity.id) || null;

/**
 * A user or division address, used for shipping and billing during checkout.
 *
 * @property {number} id - (Read only) This address's unique id.
 * @property {boolean} isDivisionAddress - (Read only) Whether this address belongs to a division rather than the user.
 * @property {string} firstName - (Read only) First name of the contact at this address.
 * @property {string} lastName - (Read only) Last name of the contact at this address.
 * @property {string} company - (Read only) Company at this address.
 * @property {string} phone - (Read only) Contact phone number for this address.
 * @property {string} address - (Read only) Street address.
 * @property {string} address2 - (Read only) Second street address line.
 * @property {string} city - (Read only) City.
 * @property {?State} state - (Read only) The state (region) for this address. Null if the country has no regions.
 * @property {?Country} country - (Read only) The country for this address.
 * @property {string} postalCode - (Read only) Postal code.
 * @property {?string} addressType - (Read only) The address type, e.g. "SHIP_TO", "BILL_TO", "BLIND_FROM".
 * @property {string} label - (Read only) The storefront display name for this address, when the storefront defines one.
 */

class UserAddress {
    /**
     * Encode an address id into the prefixed string form the checkout API expects. The API takes
     * `orderShipmentsShipToAddress`, `orderShipmentsShipFromAddress` and `billingAddressId` as prefixed
     * strings rather than numeric ids, so that a user address and a division address with the same id
     * remain distinguishable.
     *
     * @param {number} id - The address id.
     * @param {boolean} [isDivisionAddress=false] - Whether the id refers to a division address.
     * @returns {string} The prefixed id, e.g. "ADD_1234" or "DIV_1234".
     */

    static encodePrefixedId (id, isDivisionAddress = false) {
        return `${isDivisionAddress ? DIVISION_ADDRESS_PREFIX : USER_ADDRESS_PREFIX}${id}`;
    }

    /**
     * Encode an address into its prefixed id string.
     *
     * @param {UserAddress} address - The address to encode.
     * @returns {string} The prefixed id, e.g. "ADD_1234".
     */

    static toPrefixedId (address) {
        return UserAddress.encodePrefixedId(address.id, address.isDivisionAddress);
    }

    /**
     * Decode a prefixed address id back into its parts.
     *
     * @param {string} prefixedId - A prefixed id, e.g. "DIV_1234".
     * @returns {?Collaterate.User~decodedAddressId} The decoded parts, or null if the string is not a valid prefixed id.
     */

    static parsePrefixedId (prefixedId) {
        const match = PREFIXED_ID_PATTERN.exec(nullSafeToString(prefixedId));

        if (!match) {
            return null;
        }

        return Object.freeze({
            id: parseInt(match.groups.id, 10),
            isDivisionAddress: match.groups.prefix === DIVISION_ADDRESS_PREFIX,
        });
    }

    /**
     * Whether two addresses refer to the same stored address.
     *
     * A user address and a division address can share a numeric id, so both parts have to match.
     * Compares identity only, not contents, so a freshly fetched copy matches the one it replaced.
     *
     * @param {UserAddress|Object} address - An address.
     * @param {UserAddress|Object} other - The address to compare it against.
     * @returns {boolean}
     */

    static matches (address, other) {
        return Boolean(address) && Boolean(other)
            && address.id === other.id
            && Boolean(address.isDivisionAddress) === Boolean(other.isDivisionAddress);
    }

    /**
     * Build the write payload for creating an address (UserAddressDTO).
     *
     * Accepts either a {@link UserAddress UserAddress} or a plain object, so consumers can pass a form
     * value straight through. Country and state may be given as ids or as objects.
     *
     * @param {UserAddress|Object} address - The address to serialize.
     * @returns {Object} The request payload.
     */

    static toDB (address) {
        return {
            firstName: nullSafeToString(address.firstName),
            lastName: nullSafeToString(address.lastName),
            company: nullSafeToString(address.company),
            phone: nullSafeToString(address.phone),
            address: nullSafeToString(address.address),
            address2: nullSafeToString(address.address2),
            city: nullSafeToString(address.city),
            zip: nullSafeToString(address.postalCode || address.zip),
            countryId: toId(address.countryId, address.country),
            stateId: toId(address.stateId, address.state),
            addressType: address.addressType || null,
        };
    }

    /**
     * Build the write payload for updating an address (UserAddressRequestDTO), which additionally
     * requires the prefixed id form.
     *
     * @param {UserAddress|Object} address - The address to serialize.
     * @returns {Object} The request payload.
     */

    static toUpdateDB (address) {
        return {
            ...UserAddress.toDB(address),
            idWithPrefix: UserAddress.encodePrefixedId(address.id, address.isDivisionAddress),
        };
    }

    constructor (data = {}) {
        this.id = data.id;
        this.isDivisionAddress = Boolean(data.isDivisionAddress);
        this.firstName = nullSafeToString(data.firstName);
        this.lastName = nullSafeToString(data.lastName);
        this.company = nullSafeToString(data.company);
        this.phone = nullSafeToString(data.phone);
        this.address = nullSafeToString(data.address);
        this.address2 = nullSafeToString(data.address2);
        this.city = nullSafeToString(data.city);
        this.state = data.state ? new State(data.state) : null;
        this.country = data.country ? new Country(data.country) : null;
        this.postalCode = nullSafeToString(data.zip);
        this.addressType = data.addressType || null;
        this.label = nullSafeToString(data.storefrontListItemName);

        Object.freeze(this);
    }
}

export default UserAddress;

/**
 * The decoded parts of a prefixed address id.
 *
 * @typedef {Object} Collaterate.User~decodedAddressId
 * @property {number} id - The numeric address id.
 * @property {boolean} isDivisionAddress - Whether the id referred to a division address.
 */
